import {Injectable} from '@angular/core';
import {RxdbRepository} from "../../rxdb.repository";
import {cloneDeep} from 'lodash-es';
import {deepCloneListWithPrototype} from 'src/app/core/utils/clone-utils';
import {QuoteSummary} from 'src/app/core/models/quote/quote-summary';
import {Page} from 'src/app/core/utils/page';
import {QuoteWrapper} from 'src/app/core/models/quote/quote-wrapper';
import * as uuid from 'uuid';
import {from, Observable} from 'rxjs';
import {filter, switchMap} from 'rxjs/operators';

@Injectable({
    providedIn: 'root'
})
export class RxdbQuoteRepository {

    private collectionName: string;
    constructor(private readonly rxdbRepository: RxdbRepository) {
        this.init();
    }

    private init() {
        this.collectionName = 'quotes'
    }

    async saveQuote(wrapper: any): Promise<any> {
        wrapper._id = uuid.v4();
        wrapper.id = wrapper._id;
        wrapper.offlineId = wrapper._id;
        wrapper.creationDate = new Date().toISOString();
        let db = await this.rxdbRepository.initDb()
        const quote = await db[this.collectionName].insert(wrapper);

        return cloneDeep(quote._data);
    }

    async getStandaloneQuotes(pageable: Map<string, string>): Promise<Page<QuoteSummary>> {
        const page = pageable.has('page') ? parseInt(pageable.get('page')!) : 0;
        const size = pageable.has('size') ? parseInt(pageable.get('size')!) : 10;

        let db = await this.rxdbRepository.initDb();

        const selector = {
            $or: [
                { eforceOpportunityId: { $exists: false } },
                { eforceOpportunityId: null }
            ]
        };

        const pagedDocs = await db[this.collectionName]
            .find({
                selector,
                sort: [{ creationDate: 'desc' }],
                skip: page * size,
                limit: size
            })
            .exec();

        const totalDocumentsCount = await db[this.collectionName]
            .count({ selector: {} })
            .exec();

        return {
            totalElements: totalDocumentsCount,
            totalPages: Math.ceil(totalDocumentsCount / size),
            first: page === 0,
            last: (page + 1) * size >= totalDocumentsCount,
            size: size,
            content: deepCloneListWithPrototype(pagedDocs.map(d => d._data), QuoteSummary),
            number: page,
            numberOfElements: pagedDocs.length,
            empty: pagedDocs.length === 0
        };
    }

    async getQuoteById(id: string): Promise<QuoteWrapper> {
        const db = await this.rxdbRepository.initDb();
        const doc = await db[this.collectionName].findOne(id).exec();
        const quote = doc?.toMutableJSON() as QuoteWrapper;

        const json = quote as any;
        json.employee = { ...(json.employee ?? {}) };

        const fieldsToCopy = ["roleCode", "regionCode", "regionLabel", "teamCode", "teamLabel"];

        for (const field of fieldsToCopy) {
            if (!(field in json.employee) && json[field] !== undefined) {
                json.employee[field] = json[field];
            }
        }

        return quote;
    }

    watchQuoteChanges(): Observable<any> {
        return from(this.rxdbRepository.initDb()).pipe(
            switchMap(db => db['quotes'].$),
            filter((changeEvent: any) => changeEvent?.operation === 'UPDATE' && !!changeEvent?.documentData?.reference)
        );
    }

    async getQuotesByEforceOpportunityId(eforceOpportunityId: string): Promise<QuoteSummary[]> {
        let db = await this.rxdbRepository.initDb();
        const quotes = await db[this.collectionName]
            .find({
                selector: { eforceOpportunityId: eforceOpportunityId },
                sort: [{ creationDate: 'desc' }],
            })
            .exec();

        return deepCloneListWithPrototype(quotes.map(doc => doc._data), QuoteSummary);
    }

}
